/**
 * Pendo Page + Feature hooks for Download Center (PRD §11).
 *
 * Class names stay "Download Center" / `pendo-dc-*` so Pendo config can match
 * the PRD verbatim. Knowledge Center + Audience Library are not in §11 yet.
 *
 * Generate/revoke shareable-link Features have class names reserved below but
 * no UI (struck in TUN-3719). "Click DC Link from Email" is a Track Event
 * because the click happens in the mail client, not the app.
 */

import type { LocationQuery } from "vue-router";

export const PENDO_DC = {
  page: "pendo-download-center-page",
  open: "pendo-dc-open",
  search: "pendo-dc-search",
  filter: "pendo-dc-filter",
  sort: "pendo-dc-sort",
  redownload: "pendo-dc-redownload",
  delete: "pendo-dc-delete",
  rename: "pendo-dc-rename",
  openShareModal: "pendo-dc-open-share-modal",
  namedShare: "pendo-dc-named-share",
  generateLink: "pendo-dc-generate-link",
  revokeLink: "pendo-dc-revoke-link",
  openSharedFromNotification: "pendo-dc-open-shared-from-notification",
  openAmUpload: "pendo-dc-open-am-upload",
  completeAmUpload: "pendo-dc-complete-am-upload",
  associateSurvey: "pendo-dc-associate-survey",
  sendAmEmail: "pendo-dc-send-am-email",
  toggleAttachment: "pendo-dc-toggle-attachment",
  toggleDcLink: "pendo-dc-toggle-dc-link",
  saveTemplate: "pendo-dc-save-template",
  loadTemplate: "pendo-dc-load-template",
  clickSurveyLink: "pendo-dc-click-survey-link",
} as const;

/** Keep in sync with platform_api.am_delivery_email.DOWNLOADS_EMAIL_SOURCE. */
export const DOWNLOAD_CENTER_EMAIL_SOURCE = "download_center_email";

export const DOWNLOAD_CENTER_EMAIL_TRACK_EVENT = "Click DC Link from Email";

export const FILE_SHARED_NOTIFICATION_SUBJECT = "File shared with you";

export const isFileSharedNotification = (notification: {
  subject?: string;
  meta_data?: { route?: string } | null;
}): boolean =>
  notification.subject === FILE_SHARED_NOTIFICATION_SUBJECT &&
  notification.meta_data?.route === "download_center";

const querySource = (
  query: LocationQuery | null | undefined,
): string | undefined => {
  const value = query?.source;
  if (Array.isArray(value)) return value[0] ?? undefined;
  return value ?? undefined;
};

export const trackDownloadCenterPendoEvent = (
  eventName: string,
  properties?: Record<string, string>,
): void => {
  window.pendo?.track?.(eventName, properties);
};

export const consumeDownloadCenterEmailEntry = (
  query: LocationQuery | null | undefined,
  strip: () => void,
): boolean => {
  if (querySource(query) !== DOWNLOAD_CENTER_EMAIL_SOURCE) return false;
  trackDownloadCenterPendoEvent(DOWNLOAD_CENTER_EMAIL_TRACK_EVENT, {
    source: DOWNLOAD_CENTER_EMAIL_SOURCE,
  });
  strip();
  return true;
};
